import React, { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { FaTrash } from "react-icons/fa";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import ProductNavbar from "./ProductNavbar";

function ImageDropzone({ file, onFileChange, onRemove, isFeatured }) {
  const onDrop = useCallback(
    (acceptedFiles) => {
      if (!acceptedFiles.length) return;
      const newFile = Object.assign(acceptedFiles[0], {
        preview: URL.createObjectURL(acceptedFiles[0]),
      });
      onFileChange(newFile);
    },
    [onFileChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  const handleRemove = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "This image will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        onRemove(file);
      }
    });
  };

  return (
    <div
      {...getRootProps()}
      className="border border-dashed d-flex align-items-center justify-content-center"
      style={{
        width: "160px",
        height: "160px",
        borderRadius: "6px",
        position: "relative",
        cursor: "pointer",
        borderColor: isFeatured ? "#28a745" : "#ccc",
      }}
    >
      <input {...getInputProps()} />
      {file ? (
        <>
          <img
            src={file.preview || (file.image_path ? imgurl + file.image_path : "")}
            alt="preview"
            style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "6px" }}
          />
          {isFeatured && (
            <span
              style={{
                position: "absolute",
                bottom: "2px",
                left: "2px",
                backgroundColor: "#28a745",
                color: "#fff",
                fontSize: "10px",
                padding: "1px 4px",
                borderRadius: "4px",
              }}
            >
              Featured
            </span>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRemove();
            }}
            style={{
              position: "absolute",
              top: "-6px",
              right: "-6px",
              background: "#ff4d4f",
              border: "none",
              borderRadius: "50%",
              width: "22px",
              height: "22px",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <FaTrash size={14} />
          </button>
        </>
      ) : (
        <p style={{ fontSize: "12px", textAlign: "center" }}>{isDragActive ? "Drop here" : "Add Product Image"}</p>
      )}
    </div>
  );
}

function ProductGallery() {
  const { uuid } = useParams();
  const MAX_IMAGES = 10;
  const [gallery, setGallery] = useState(Array(MAX_IMAGES).fill(null));

  // Fetch existing images on load
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const res = await axios.get(`${baseurl}getProductGallery/${uuid}`);
        const images = res.data.images || [];
        const galleryArray = Array(MAX_IMAGES).fill(null);
        images.forEach((img, idx) => {
          galleryArray[idx] = img; // existing images with image_path
        });
        setGallery(galleryArray);
      } catch (err) {
        console.error("Error fetching gallery:", err);
      }
    };
    fetchImages();
  }, [uuid]);

  const handleGalleryChange = (index, file) => {
    const newGallery = [...gallery];
    newGallery[index] = file;
    setGallery(newGallery);
  };

  const handleGalleryRemove = async (file) => {
    if (file.uuid) {
      // Existing image, delete from backend
      try {
        await axios.delete(`${baseurl}deleteProductGalleryImage/${file.uuid}`);
        setGallery(gallery.map((g) => (g && g.uuid === file.uuid ? null : g)));
      } catch (err) {
        console.error("Error deleting image:", err);
      }
    } else {
      // New file, just remove from state
      setGallery(gallery.map((g) => (g === file ? null : g)));
    }
  };

  const [submitting, setSubmitting] = useState(false);

const handleSubmit = async (e, redirectNext = false) => {
  e.preventDefault();
  setSubmitting(true); // start loader

  const formData = new FormData();
  formData.append("product_uuid", uuid);
  gallery.forEach((file) => {
    if (file && !file.uuid) formData.append("gallery", file); // only new files
  });

  try {
    const res = await axios.post(`${baseurl}addUpdateProductGallery/${uuid}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    Swal.fire({
      position: "top-end",
      icon: "success",
      title: "Images uploaded successfully!",
      showConfirmButton: false,
      timer: 2000,
      toast: true,
    });

    // update gallery state
    const updatedGallery = Array(MAX_IMAGES).fill(null);
    res.data.images.forEach((img, idx) => (updatedGallery[idx] = img));
    setGallery(updatedGallery);

    // Redirect if "Update & Next"
    if (redirectNext) {
      setTimeout(() => {
        window.location.href = `/variant/${uuid}`; // change this to your next page URL
      }, 500);
    }
  } catch (err) {
    console.error("Error uploading gallery:", err);
    Swal.fire({
      position: "top-end",
      icon: "error",
      title: "Failed to upload images",
      showConfirmButton: false,
      timer: 2500,
      toast: true,
    });
  } finally {
    setSubmitting(false); // stop loader
  }
};


  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper">
        <div className="main-content">

<ProductNavbar/>

          <div className="col-12 mb-2">
            <p style={{ fontSize: "12px", color: "#555" }}>
              Note: The first image is considered the <strong>Featured Image</strong>.
            </p>
          </div>
          <div className="col-12 mb-4">
            <div className="card">
              <div className="card-header">Upload Product Images</div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {gallery.map((file, i) => (
                      <ImageDropzone
                        key={i}
                        file={file}
                        onFileChange={(f) => handleGalleryChange(i, f)}
                        onRemove={handleGalleryRemove}
                        isFeatured={i === 0 && file !== null}
                      />
                    ))}
                  </div>
                  <div className="text-end">
  <button
    type="button"
    className="btn btn-secondary me-2"
    onClick={(e) => handleSubmit(e, false)}
    disabled={submitting}
  >
    {submitting ? (
      <>
        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
        Updating...
      </>
    ) : (
      "Update"
    )}
  </button>

  <button
    type="button"
    className="btn btn-primary"
    onClick={(e) => handleSubmit(e, true)}
    disabled={submitting}
  >
    {submitting ? (
      <>
        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
        Updating...
      </>
    ) : (
      "Update & Next"
    )}
  </button>
</div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default ProductGallery;
